import * as Constants from '../constants';

const initialState = {
  // Service metadata
  service: {
    serviceId: null,
    serviceName: '',
    displayName: '',
    categoryId: null,
    vendorId: '',
    vendorName: '',
    routePrefix: '',
    hasSlots: false,
  },

  // Categories & Subcategories metadata
  categoriesData: null,
  categoriesLoading: false,
  categoriesError: null,

  // Tab 1: Package selection
  packages: {
    data: [],
    loading: false,
    error: null,
    batchPending: 0,  // tracks how many subcategory calls are still in-flight
  },
  selectedPackage: null,
  itemVariants: {
    data: [],
    loading: false,
  },

  // Tab 2: Slot & Patient
  slots: {
    dates: [],
    groups: [],
    data: [],
    loading: false,
    error: null,
  },
  selectedSlot: {
    date: '',
    slotId: '',
    startTime: '',
    endTime: '',
    filter: '',
  },

  // Pricing
  bifurcationData: null,
  bifurcationLoading: false,

  // Order
  orderId: null,
  orderData: null,
  orderLoading: false,
  orderError: null,
};

export default function servicePlatformReducer(state = initialState, action) {
  switch (action.type) {
    case Constants.RESET_SERVICE_PLATFORM:
      return initialState;

    case Constants.SET_SERVICE_DETAILS:
      return {
        ...state,
        service: {
          ...state.service,
          ...action.payload,
        },
      };

    case Constants.FETCH_SERVICE_ITEM_DETAILS_START:
      return state;

    case Constants.FETCH_SERVICE_ITEM_DETAILS_SUCCESS:
    case Constants.FETCH_SERVICE_ITEM_DETAILS_FAILURE: {
      const itemData = (action.data && action.data.data) || action.data || {};
      const vendor = itemData.vendor || {};
      const vendorId = (vendor.attributes && vendor.attributes.actualVendorId) || vendor.vendorId || '';
      const vendorName = vendor.name || '';
      const attributes = itemData.attributes || {};
      const hasSlots = attributes.showSlot !== false;
      return {
        ...state,
        service: {
          ...state.service,
          hasSlots,
          ...(vendorId ? { vendorId } : {}),
          ...(vendorName ? { vendorName } : {}),
        },
      };
    }

    // Categories & Subcategories
    case Constants.FETCH_CATEGORIES_SUBCATEGORIES_START:
      return {
        ...state,
        categoriesLoading: true,
        categoriesError: null,
      };

    case Constants.FETCH_CATEGORIES_SUBCATEGORIES_SUCCESS: {
      const catData = action.data && action.data.data;
      const serviceName = catData && catData.serviceName;
      return {
        ...state,
        categoriesData: catData,
        categoriesLoading: false,
        categoriesError: null,
        ...(serviceName ? { service: { ...state.service, serviceName } } : {}),
      };
    }

    case Constants.FETCH_CATEGORIES_SUBCATEGORIES_FAILURE:
      return {
        ...state,
        categoriesLoading: false,
        categoriesError: (action.data && action.data.error) || 'Failed to fetch categories',
      };

    // Package selection
    case Constants.FETCH_SERVICE_PACKAGES_START:
      return {
        ...state,
        packages: {
          ...state.packages,
          loading: true,
          error: null,
        },
      };

    case Constants.FETCH_SERVICE_PACKAGES_SUCCESS:
      return {
        ...state,
        packages: {
          data: action.data || [],
          loading: false,
          error: null,
        },
      };

    case Constants.FETCH_SERVICE_PACKAGES_FAILURE:
      return {
        ...state,
        packages: {
          ...state.packages,
          loading: false,
          error: (action.data && action.data.error) || 'Failed to fetch packages',
        },
      };

    // one action per subcategory, batchPending tracks how many are left
    case Constants.FETCH_SERVICE_PACKAGES_BATCH_START:
      return {
        ...state,
        packages: {
          data: [],
          loading: true,
          error: null,
          batchPending: action.total,
        },
      };

    case Constants.FETCH_SERVICE_PACKAGES_BATCH_ITEM_START:
      return state;

    case Constants.FETCH_SERVICE_PACKAGES_BATCH_ITEM_SUCCESS: {
      const items = (action.data && action.data.data && action.data.data.items) || [];
      const remaining = state.packages.batchPending - 1;
      // Merge new items, deduplicate by id
      const merged = state.packages.data.concat(items).filter(
        (item, idx, self) => idx === self.findIndex(t => t.id === item.id)
      );
      return {
        ...state,
        packages: {
          ...state.packages,
          data: merged,
          loading: remaining > 0,
          batchPending: remaining,
        },
      };
    }

    case Constants.FETCH_SERVICE_PACKAGES_BATCH_ITEM_FAILURE: {
      const remaining = state.packages.batchPending - 1;
      return {
        ...state,
        packages: {
          ...state.packages,
          loading: remaining > 0,
          batchPending: remaining,
        },
      };
    }

    case Constants.SET_SELECTED_SERVICE_PACKAGE:
      return {
        ...state,
        selectedPackage: action.payload,
      };

    // Item variants (session picker)
    case Constants.FETCH_SERVICE_ITEM_VARIANTS_START:
      return {
        ...state,
        itemVariants: { data: [], loading: true },
      };

    case Constants.FETCH_SERVICE_ITEM_VARIANTS_SUCCESS: {
      const items = (action.data && action.data.data && action.data.data.items) || [];
      return {
        ...state,
        itemVariants: { data: items, loading: false },
      };
    }

    case Constants.FETCH_SERVICE_ITEM_VARIANTS_FAILURE:
      return {
        ...state,
        itemVariants: { data: [], loading: false },
      };

    // Slot selection
    case Constants.FETCH_SERVICE_SLOTS_START:
      return {
        ...state,
        slots: {
          ...state.slots,
          loading: true,
          error: null,
        },
      };

    case Constants.FETCH_SERVICE_SLOTS_SUCCESS: {
      const slotData = (action.data && action.data.data && action.data.data.slotData) || [];
      const sectionInfo = (action.data && action.data.data && action.data.data.sectionInfo) || {};
      return {
        ...state,
        slots: {
          dates: slotData.map(d => d.date),
          data: slotData,
          loading: false,
          error: null,
          sectionInfo,
        },
      };
    }

    case Constants.FETCH_SERVICE_SLOTS_FAILURE:
      return {
        ...state,
        slots: {
          ...state.slots,
          loading: false,
          error: action.payload,
        },
      };

    case Constants.SET_SELECTED_SERVICE_DATE:
      return {
        ...state,
        selectedSlot: {
          ...state.selectedSlot,
          date: action.data,
        },
      };

    case Constants.SET_SELECTED_SERVICE_SLOT:
      return {
        ...state,
        selectedSlot: {
          ...state.selectedSlot,
          ...action.data,
        },
      };

    case Constants.SET_SELECTED_SERVICE_FILTER:
      return {
        ...state,
        selectedSlot: {
          ...state.selectedSlot,
          filter: action.data,
        },
      };

    // Bifurcation
    case Constants.FETCH_SERVICE_BIFURCATION_START:
      return {
        ...state,
        bifurcationLoading: true,
      };

    case Constants.FETCH_SERVICE_BIFURCATION_SUCCESS:
      return {
        ...state,
        bifurcationData: action.data,
        bifurcationLoading: false,
      };

    case Constants.FETCH_SERVICE_BIFURCATION_FAILURE: {
      // bifurcation API doesn't send status:1, so middleware routes it here even on success
      const bifData = action.data || {};
      if (bifData.amountBifurcation) {
        return {
          ...state,
          bifurcationData: bifData,
          bifurcationLoading: false,
        };
      }
      return {
        ...state,
        bifurcationLoading: false,
      };
    }

    // Order creation
    case Constants.CREATE_SERVICE_ORDER_START:
      return {
        ...state,
        orderLoading: true,
        orderError: null,
      };

    case Constants.CREATE_SERVICE_ORDER_SUCCESS: {
      const resData = action.data || {};
      const oid = resData.orderId || (resData.data && resData.data.orderId) || null;
      return {
        ...state,
        orderId: oid,
        orderData: resData.data || resData,
        orderLoading: false,
        orderError: null,
      };
    }

    case Constants.CREATE_SERVICE_ORDER_FAILURE: {
      const resData = action.data || {};
      // Service platform API may return orderId even in "failure" path
      // (middleware treats status !== 1 as failure, but order may have been placed)
      const oid = resData.orderId || (resData.data && resData.data.orderId) || null;
      if (oid) {
        // Order was actually created — treat as success
        return {
          ...state,
          orderId: oid,
          orderData: resData.data || resData,
          orderLoading: false,
          orderError: null,
        };
      }
      return {
        ...state,
        orderLoading: false,
        orderError: (resData.message) || (resData.error && resData.error.message) || 'Order creation failed',
      };
    }

    default:
      return state;
  }
}
